import {
  autoPlacement,
  FloatingPortal,
  offset,
  Placement,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useTransitionStyles,
} from '@floating-ui/react'
import { ChevronRight } from '@mui/icons-material'
import { Tooltip, Typography } from '@mui/material'
import { HTMLAttributes, ReactNode, useState } from 'react'
import { navItemStyle } from 'src/features/globalNavigation/components/navItem/navItemStyle'

type NavItemSubMenuOpenerProps = {
  /**
   * サブメニューのコンテンツ
   */
  renderSubmenu: (closeSubMenu: () => void) => ReactNode
  icon: ReactNode
  /**
   * 折り畳まれているかどうか
   */
  isCollapsed: boolean
  title: string
  placement?: Array<Placement>
} & HTMLAttributes<HTMLElement>

/**
 * グローバルナビゲーションの個々のアイテム（ホバー時及びタップ時にサブメニューを表示するタイプ）
 */
export const NavItemSubMenuOpener: React.FC<NavItemSubMenuOpenerProps> = (
  props: NavItemSubMenuOpenerProps,
) => {
  const { icon, isCollapsed, renderSubmenu, title, placement, ...restProps } =
    props

  const [isOpen, setIsOpen] = useState(false)
  const closeSubMenu = () => setIsOpen(false)

  const { context, refs, floatingStyles } = useFloating({
    middleware: [
      // フローティング要素が見切れる場合には位置を上にずらして調整する
      autoPlacement({
        allowedPlacements: placement ? placement : ['right-start', 'right-end'],
      }),
      // フローティング要素とトリガー要素の隙間
      offset(16),
    ],
    strategy: 'fixed',
    open: isOpen,
    onOpenChange: setIsOpen,
  })

  // クリック時やタップ時にフローティング要素を開く。
  const click = useClick(context)

  // ESCボタン押下時や、トリガー要素やフローティング要素の外側をクリックした時に、フローティング要素を閉じる
  const dismiss = useDismiss(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ])

  // フローティング要素の開閉時に透過率をアニメーションさせる。
  // isMountedはisOpenのラッパーであり、閉じるアニメーションが終わるまでfalseになるのを遅延させてくれるもの。
  const { isMounted, styles: animatedStyles } = useTransitionStyles(context)

  // 開閉状態に応じて表示内容を切り替える
  let content: ReactNode
  if (isCollapsed) {
    content = <>{icon}</>
  } else {
    content = (
      <>
        {icon}
        <Typography variant="subtitle2">{title}</Typography>
        <ChevronRight
          fontSize="small"
          sx={{ marginLeft: 'auto', width: '20px', height: '20px' }}
        />
      </>
    )
  }

  return (
    <>
      <Tooltip title={isCollapsed ? title : ''} placement="right" arrow>
        {/* トリガー要素 */}
        <div
          {...restProps}
          {...getReferenceProps()}
          css={[
            navItemStyle.container,
            isCollapsed && navItemStyle.containerCollapsed,
          ]}
          ref={refs.setReference}
          style={{
            height: 'auto',
            minHeight: '40px',
          }}
        >
          {content}
        </div>
      </Tooltip>

      {/* フローティング要素 */}
      {isMounted && (
        <FloatingPortal>
          <div
            {...getFloatingProps()}
            ref={refs.setFloating} // eslint-disable-line react-hooks/refs -- FIXME: eslint-plugin-react-hooks が 7.0.1 にバージョンアップした際にエラーになるようになったため一旦無視。ここのコードを触る際に修正してください🙏
            style={{
              ...floatingStyles,
              ...animatedStyles,
              // ページ内の sticky なテーブルヘッダー等より前、MUI の Drawer(1200) や Modal(1300) より後ろに出す
              zIndex: 1000,
            }}
          >
            {renderSubmenu(closeSubMenu)}
          </div>
        </FloatingPortal>
      )}
    </>
  )
}
